import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import { apiFetch } from '@/api/client';
import { useVendors } from '@/api/masters';
import { DatePicker } from '@/components/DatePicker';
import { Field, controlClass, selectClass } from '@/components/Field';
import { Card, Notice, SectionTitle } from '@/components/PageHeading';
import { SearchPicker, type PickerOption } from '@/components/SearchPicker';
import { StatusBadge } from '@/components/StatusBadge';
import type { ItemVariantAxisOptions } from '@core/api/masters';
import { stockImportsPath } from '@core/api/queryPaths';
import type { StockImport } from '@core/api/stockImport';
import { toDayjs, toIsoDate } from '@core/format/dates';
import { formatAmount } from '@core/format/money';
import { canAct } from '@core/session/capability';
import type { Session } from '@core/session/types';
import {
  attributeField,
  fieldLabels,
  fixedFields,
  wordsToMatch,
  type Choosable,
  type FieldKey,
  type ImportPlan,
  type PlannedRow,
  type Sheet,
  type WordChoice,
  type WordMap,
} from '@core/units/stockImport';

import { UndoImport } from './ImportDone';
import { importBlocker, type SavedLayout } from './importRules';
import { saveWorkbook, type ReadWorkbook } from './workbook';

export type PhotoMode = 'sheet' | 'files' | 'links' | 'none';

/* ====================================================================== 1 · File */

export function FileStep({
  session,
  book,
  reading,
  problem,
  sheetIndex,
  headerRow,
  sheet,
  photoMode,
  photoFiles,
  layoutFound,
  onFile,
  onSheet,
  onHeaderRow,
  onPhotoMode,
  onPhotoFiles,
}: {
  session: Session;
  book: ReadWorkbook | null;
  reading: boolean;
  problem: string | null;
  sheetIndex: number;
  headerRow: number;
  sheet: Sheet | null;
  photoMode: PhotoMode;
  photoFiles: Map<string, File>;
  layoutFound: SavedLayout | null;
  onFile: (file: File) => void;
  onSheet: (index: number) => void;
  onHeaderRow: (row: number) => void;
  onPhotoMode: (mode: PhotoMode) => void;
  onPhotoFiles: (files: File[]) => void;
}) {
  const pictures = book?.sheets[sheetIndex]?.pictureRows.size ?? 0;
  const chosenFiles = new Set(photoFiles.values()).size;

  return (
    <div className="grid grid-cols-12 gap-3 lg:gap-4">
      <div className="col-span-12 flex flex-col gap-3 lg:gap-4 xl:col-span-8">
        <Card>
          {book ? (
            <div className="bg-primary-transparent flex flex-wrap items-center gap-3 rounded-md p-3">
              <span className="flex h-12 w-10 shrink-0 items-center justify-center rounded bg-[#107c41] text-[11px] font-bold text-[#ffffff]">
                {book.fileName.toLowerCase().endsWith('.csv') ? 'CSV' : 'XLSX'}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold text-gray-900">
                  {book.fileName}
                </span>
                <span className="text-default block text-[13px]">
                  {`${book.sheets.length} sheet${book.sheets.length === 1 ? '' : 's'} · ${sizeLabel(book.sizeBytes)} · opened on this computer`}
                </span>
              </span>
              <FilePick label="Choose another" busy={reading} onFile={onFile} />
            </div>
          ) : (
            <label className="border-border-color hover:bg-light flex cursor-pointer flex-col items-center gap-2 rounded-md border-2 border-dashed px-4 py-10 text-center">
              <i className="icon icon-file-spreadsheet text-primary text-[28px]" />
              <span className="text-sm font-semibold text-gray-900">
                {reading ? 'Opening the file…' : 'Choose the Excel file'}
              </span>
              <span className="text-default text-[13px]">
                .xlsx or .csv — the layout the shop already keeps. It is opened here; nothing is
                sent until the last step.
              </span>
              <input
                type="file"
                accept=".xlsx,.csv,.xls"
                className="hidden"
                disabled={reading}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  event.target.value = '';

                  if (file) {
                    onFile(file);
                  }
                }}
              />
            </label>
          )}

          {problem && (
            <div className="mt-3">
              <Notice tone="danger" title="That file cannot be imported">
                {problem}
              </Notice>
            </div>
          )}

          {book && sheet && (
            <div className="mt-4 flex flex-col gap-4">
              {book.sheets.length > 1 && (
                <div className="flex flex-col gap-2">
                  <span className="text-default text-[11px] font-bold tracking-[0.12em] uppercase">
                    Which sheet
                  </span>
                  <div className="flex flex-wrap gap-2">
                    {book.sheets.map((candidate, index) =>
                      index === sheetIndex ? (
                        <div
                          key={candidate.name}
                          className="border-primary bg-primary-transparent rounded-md border-2 px-3.5 py-2 text-sm font-semibold text-gray-900"
                        >
                          {`${candidate.name} · ${candidate.grid.length} rows`}
                        </div>
                      ) : (
                        <button
                          key={candidate.name}
                          type="button"
                          onClick={() => onSheet(index)}
                          className="border-border-color hover:bg-light cursor-pointer rounded-md border bg-white px-3.5 py-2 text-sm text-gray-900"
                        >
                          {`${candidate.name} · ${candidate.grid.length} rows`}
                        </button>
                      ),
                    )}
                  </div>
                </div>
              )}

              <div className="flex flex-wrap items-center gap-3">
                <label htmlFor="import-heading-row" className="text-sm text-gray-900">
                  Headings are on row
                </label>
                <input
                  id="import-heading-row"
                  type="number"
                  min={1}
                  max={50}
                  value={headerRow + 1}
                  onWheel={(event) => event.currentTarget.blur()}
                  onChange={(event) => {
                    const row = Number(event.target.value);

                    if (Number.isInteger(row) && row >= 1 && row <= 50) {
                      onHeaderRow(row - 1);
                    }
                  }}
                  className={`${controlClass} max-w-20`}
                />
                <span className="text-default text-[13px]">
                  {headerRow === 0
                    ? 'The first row holds the headings.'
                    : `Rows above it (a title, a date) are not read.`}
                </span>
              </div>

              <div className="border-border-color overflow-x-auto rounded-md border">
                <table className="w-full border-collapse text-[13px] whitespace-nowrap">
                  <thead>
                    <tr className="bg-light text-start">
                      {sheet.headers.map((heading, index) => (
                        <th
                          key={index}
                          className="px-3 py-2 text-start font-semibold text-gray-900"
                        >
                          {heading}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sheet.rows.slice(0, 4).map((row) => (
                      <tr key={row.number} className="border-border-color border-t">
                        {row.cells.map((value, index) => (
                          <td key={index} className="max-w-48 truncate px-3 py-2 text-gray-900">
                            {value}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <span className="text-default -mt-2 text-[13px]">
                {`The first rows, exactly as they are in the sheet · ${sheet.rows.length} rows below the headings.`}
              </span>
            </div>
          )}
        </Card>

        {book && sheet && (
          <Card>
            <SectionTitle>Where are the photos?</SectionTitle>
            <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
              <PhotoTile
                selected={photoMode === 'sheet'}
                disabled={pictures === 0}
                title="Pictures inside the sheet"
                hint={
                  pictures === 0
                    ? 'None found on this sheet'
                    : `${pictures} found, each belonging to the row it sits on`
                }
                onChoose={() => onPhotoMode('sheet')}
              />
              <PhotoTile
                selected={photoMode === 'files'}
                title="A column of file names"
                hint={
                  photoMode === 'files'
                    ? `${chosenFiles} photo${chosenFiles === 1 ? '' : 's'} chosen`
                    : 'Then choose the photos alongside'
                }
                onChoose={() => onPhotoMode('files')}
              />
              <PhotoTile
                selected={photoMode === 'links'}
                title="A column of web links"
                hint="Kept as the new design's photo link"
                onChoose={() => onPhotoMode('links')}
              />
              <PhotoTile
                selected={photoMode === 'none'}
                title="No photos now"
                hint="Add them from the design later"
                onChoose={() => onPhotoMode('none')}
              />
            </div>
            {photoMode === 'files' && (
              <label className="border-border-color hover:bg-light mt-3 flex cursor-pointer items-center gap-2 rounded-md border bg-white px-3 py-2 text-[13px] font-semibold text-gray-900">
                <i className="icon icon-images" />
                {chosenFiles > 0 ? 'Choose the photos again' : 'Choose the photos'}
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(event) => {
                    onPhotoFiles([...(event.target.files ?? [])]);
                    event.target.value = '';
                  }}
                />
              </label>
            )}
            <p className="text-default mt-3 mb-0 text-[13px]">
              Photos go to designs the import creates. A design already in the catalogue keeps its
              own photos.
            </p>
          </Card>
        )}
      </div>

      <div className="col-span-12 flex flex-col gap-3 lg:gap-4 xl:col-span-4">
        {layoutFound && (
          <Card>
            <SectionTitle>A layout used before</SectionTitle>
            <div className="border-primary bg-primary-transparent rounded-md border-2 px-3.5 py-2.5">
              <span className="block text-sm font-semibold text-gray-900">{layoutFound.name}</span>
              <span className="text-default block text-[13px]">{`Same headings · used ${layoutFound.usedOn}`}</span>
            </div>
            <p className="text-default mt-2 mb-0 text-[13px]">
              The columns come matched the same way. You can still change any of them.
            </p>
          </Card>
        )}
        <Card>
          <SectionTitle>No sheet yet?</SectionTitle>
          <p className="text-default mb-2 text-[13px]">
            Start from ours — one row per design, size and colour, and a column for how many.
          </p>
          <button
            type="button"
            className="text-primary cursor-pointer p-0 text-sm font-semibold"
            onClick={() => void downloadSample()}
          >
            Download the sample sheet
          </button>
        </Card>
        <RecentImports session={session} />
      </div>
    </div>
  );
}

function FilePick({
  label,
  busy,
  onFile,
}: {
  label: string;
  busy: boolean;
  onFile: (file: File) => void;
}) {
  return (
    <label className="border-border-color hover:bg-light cursor-pointer rounded-md border bg-white px-3 py-1.5 text-[13px] text-gray-900">
      {busy ? 'Opening…' : label}
      <input
        type="file"
        accept=".xlsx,.csv,.xls"
        className="hidden"
        disabled={busy}
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';

          if (file) {
            onFile(file);
          }
        }}
      />
    </label>
  );
}

function PhotoTile({
  selected,
  disabled = false,
  title,
  hint,
  onChoose,
}: {
  selected: boolean;
  disabled?: boolean;
  title: string;
  hint: string;
  onChoose: () => void;
}) {
  const body = (
    <>
      <span className="block text-sm font-semibold text-gray-900">{title}</span>
      <span className="text-default block text-[13px]">{hint}</span>
    </>
  );

  // A selected tile is a div, not a disabled button (it stays readable and out of the tab order).
  return selected ? (
    <div className="border-primary bg-primary-transparent rounded-md border-2 px-3.5 py-3">
      {body}
    </div>
  ) : (
    <button
      type="button"
      disabled={disabled}
      onClick={onChoose}
      className="border-border-color hover:bg-light cursor-pointer rounded-md border bg-white px-3.5 py-3 text-start disabled:cursor-not-allowed disabled:opacity-60"
    >
      {body}
    </button>
  );
}

/** This branch's recent imports, each with its Undo while nothing has happened to it. */
function RecentImports({ session }: { session: Session }) {
  const recent = useQuery({
    queryKey: ['stock-import', { branchId: session.branch?.id }],
    queryFn: () => apiFetch<StockImport[]>(stockImportsPath()),
    enabled: session.branch !== null && session.branch !== undefined,
  });
  const live = (recent.data ?? []).filter((record) => record.undoneOn === null).slice(0, 5);

  if (live.length === 0) {
    return null;
  }

  return (
    <Card>
      <SectionTitle>Imported before</SectionTitle>
      <ul className="ps-0">
        {live.map((record, index) => (
          <li
            key={record.id}
            className={`list-none py-2.5 ${index > 0 ? 'border-border-color border-t' : ''}`}
          >
            <span className="block text-xs font-semibold text-gray-900">{record.fileName}</span>
            <span className="text-default block text-[11px]">
              {`${record.unitsImported} garments · ${record.designsCreated} new designs · ${record.createdOn.slice(0, 10)}`}
            </span>
            {record.canUndo && canAct(session, 'ItemUnit:Delete') && (
              <div className="mt-1.5">
                <UndoImport record={record} compact />
              </div>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}

const sampleHeaders = [
  'Design name',
  'Design code',
  'Category',
  'Size',
  'Colour',
  'How many',
  'Cost each',
  'Rent for the event',
  'Each extra day',
  'Photo',
  'Notes',
];

function downloadSample() {
  return saveWorkbook('stock-sample.xlsx', 'Stock', sampleHeaders, []);
}

function sizeLabel(bytes: number): string {
  return bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/* =================================================================== 2 · Columns */

export function ColumnsStep({
  sheet,
  mapping,
  attributes,
  categories,
  defaultCategoryId,
  layoutName,
  problems,
  onMapping,
  onDefaultCategory,
  onLayoutName,
}: {
  sheet: Sheet;
  mapping: FieldKey[];
  attributes: readonly { id: number; code: string; name: string }[];
  categories: readonly Choosable[];
  defaultCategoryId: number | null;
  layoutName: string;
  problems: string[];
  onMapping: (mapping: FieldKey[]) => void;
  onDefaultCategory: (id: number | null) => void;
  onLayoutName: (name: string) => void;
}) {
  const options: { value: FieldKey; label: string }[] = [
    ...fixedFields
      .filter((field) => field !== 'ignore')
      .map((field) => ({ value: field, label: fieldLabels[field] })),
    ...attributes.map((attribute) => ({
      value: attributeField(attribute.code),
      label: attribute.name,
    })),
    { value: 'ignore', label: fieldLabels.ignore },
  ];
  const hasCategoryColumn = mapping.includes('category');
  const pictures = sheet.rows.filter((row) => row.picture !== null).length;

  const choose = (column: number, field: FieldKey) =>
    onMapping(
      mapping.map((current, index) =>
        index === column ? field : field !== 'ignore' && current === field ? 'ignore' : current,
      ),
    );

  return (
    <div className="grid grid-cols-12 gap-3 lg:gap-4">
      <div className="col-span-12 xl:col-span-8">
        <Card flush className="py-3">
          <div className="text-default hidden grid-cols-[1.1fr_1.4fr_1.4fr] gap-3 px-5 pb-2 text-[11px] font-bold tracking-[0.12em] uppercase sm:grid">
            <span>Your column</span>
            <span>In your sheet</span>
            <span>Goes to</span>
          </div>
          <ul className="ps-0">
            {sheet.headers.map((heading, column) => {
              const field = mapping[column] ?? 'ignore';

              return (
                <li
                  key={column}
                  className={`border-border-color grid list-none grid-cols-1 items-center gap-2 border-t px-5 py-2.5 sm:grid-cols-[1.1fr_1.4fr_1.4fr] sm:gap-3 ${
                    field === 'ignore' ? 'bg-light' : ''
                  }`}
                >
                  <span className="text-sm font-semibold text-gray-900">{heading}</span>
                  <span className="text-default truncate text-[13px]">
                    {field === 'photo' && pictures > 0
                      ? `${pictures} picture${pictures === 1 ? '' : 's'} in the sheet`
                      : samplesOf(sheet, column)}
                  </span>
                  <span className="flex items-center gap-2">
                    <select
                      aria-label={`Where ${heading} goes`}
                      value={field}
                      onChange={(event) => choose(column, event.target.value as FieldKey)}
                      className={selectClass}
                    >
                      {options.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <span
                      className={`text-[12px] font-semibold whitespace-nowrap ${
                        field === 'ignore' ? 'text-default' : 'text-success'
                      }`}
                    >
                      {field === 'ignore' ? 'Not read' : 'Matched'}
                    </span>
                  </span>
                </li>
              );
            })}
          </ul>
        </Card>
      </div>

      <div className="col-span-12 flex flex-col gap-3 lg:gap-4 xl:col-span-4">
        <Card>
          <SectionTitle>What an import needs</SectionTitle>
          <ul className="ps-0 text-sm text-gray-900">
            <Need met={mapping.includes('designName') || mapping.includes('designCode')}>
              A design — its name or code
            </Need>
            <Need met={hasCategoryColumn || defaultCategoryId !== null}>
              Its category — a column, or one for every row
            </Need>
            <Need met={mapping.some((field) => field.startsWith('attr:'))}>
              What tells garments apart — size, colour
            </Need>
            <Need met={mapping.includes('quantity') || mapping.includes('serial')}>
              How many, or each garment’s own tag or reference number
            </Need>
          </ul>
          {problems.length > 0 && (
            <div className="mt-2">
              <Notice tone="warning" title="Before the next step">
                {problems.join(' ')}
              </Notice>
            </div>
          )}
          {!hasCategoryColumn && (
            <div className="mt-3">
              <Field
                label="Every row is a"
                hint="For designs the import creates — a known design keeps its own."
              >
                <select
                  value={defaultCategoryId ?? ''}
                  onChange={(event) =>
                    onDefaultCategory(event.target.value === '' ? null : Number(event.target.value))
                  }
                  className={selectClass}
                >
                  <option value="">Choose a category</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
          )}
          <p className="text-default border-border-color mt-3 mb-0 border-t pt-3 text-[13px]">
            Everything else is optional. A column left on “Ignore” is never read.
          </p>
        </Card>
        <Card>
          <SectionTitle>Keep this layout</SectionTitle>
          <Field
            label="Name it"
            hint="The next sheet with these headings comes matched. Kept on this computer."
          >
            <input
              value={layoutName}
              onChange={(event) => onLayoutName(event.target.value)}
              placeholder="Shreeji wholesaler"
              className={controlClass}
            />
          </Field>
        </Card>
      </div>
    </div>
  );
}

function Need({ met, children }: { met: boolean; children: React.ReactNode }) {
  return (
    <li className="flex list-none items-center gap-2 py-1">
      <i className={`icon ${met ? 'icon-check text-success' : 'icon-circle text-default'}`} />
      <span className={met ? '' : 'text-default'}>{children}</span>
    </li>
  );
}

function samplesOf(sheet: Sheet, column: number): string {
  const seen: string[] = [];

  for (const row of sheet.rows) {
    const value = row.cells[column] ?? '';

    if (value !== '' && !seen.includes(value)) {
      seen.push(value);
    }

    if (seen.length === 3) {
      break;
    }
  }

  return seen.length === 0 ? '(blank)' : seen.join(', ');
}

/* ===================================================================== 3 · Words */

export function WordsStep({
  session,
  sheet,
  mapping,
  words,
  plan,
  categories,
  axesByCategory,
  onWords,
}: {
  session: Session;
  sheet: Sheet;
  mapping: FieldKey[];
  words: WordMap;
  plan: ImportPlan;
  categories: readonly Choosable[];
  axesByCategory: Record<number, ItemVariantAxisOptions[] | undefined>;
  onWords: (words: WordMap) => void;
}) {
  const listed = useMemo(
    () => wordsToMatch({ sheet, mapping, words, categories, axesByCategory }),
    [sheet, mapping, words, categories, axesByCategory],
  );
  const mayAddValues = canAct(session, 'ItemAttribute:Edit');
  const known = new Set(
    plan.rows
      .filter((row) => row.status !== 'out' && row.design && 'id' in row.design)
      .map((row) => (row.design as { id: number }).id),
  ).size;
  const reading = Object.values(axesByCategory).some((axes) => axes === undefined);

  const choose = (field: string, key: string, choice: WordChoice | null) => {
    const current = { ...(words[field] ?? {}) };

    if (choice === null) {
      delete current[key];
    } else {
      current[key] = choice;
    }

    onWords({ ...words, [field]: current });
  };

  return (
    <div className="grid grid-cols-12 gap-3 lg:gap-4">
      <div className="col-span-12 xl:col-span-8">
        <Card flush className="py-3">
          <div className="px-5 pb-2">
            <SectionTitle>
              {listed.length === 0 ? 'Every word is known' : 'Not known yet'}
            </SectionTitle>
            <p className="text-default -mt-2 mb-0 text-[13px]">
              {reading
                ? 'Reading the sizes and colours each category takes…'
                : listed.length === 0
                  ? 'Every category, size and colour in the sheet matches one already set up.'
                  : 'Choose what each one means. Words that already match — a size of 40, a known colour — are not shown.'}
            </p>
          </div>
          <ul className="ps-0">
            {listed.map((word) => {
              const choice = words[word.field]?.[word.key];
              const value =
                choice === undefined
                  ? ''
                  : choice.kind === 'value'
                    ? `v:${choice.id}`
                    : choice.kind;

              return (
                <li
                  key={`${word.field}|${word.key}`}
                  className="border-border-color grid list-none grid-cols-1 items-center gap-2 border-t px-5 py-2.5 sm:grid-cols-[0.8fr_1.2fr_1.6fr] sm:gap-3"
                >
                  <span className="text-default text-[11px] font-bold tracking-[0.12em] uppercase">
                    {word.label}
                  </span>
                  <span>
                    <span className="block text-[15px] font-semibold text-gray-900">{`“${word.word}”`}</span>
                    <span className="text-default block text-[12px]">{`on ${word.rows} row${word.rows === 1 ? '' : 's'}`}</span>
                  </span>
                  <select
                    aria-label={`What ${word.word} means`}
                    value={value}
                    onChange={(event) => {
                      const next = event.target.value;

                      choose(
                        word.field,
                        word.key,
                        next === ''
                          ? null
                          : next === 'new'
                            ? { kind: 'new' }
                            : next === 'skip'
                              ? { kind: 'skip' }
                              : { kind: 'value', id: Number(next.slice(2)) },
                      );
                    }}
                    className={`${selectClass} ${value === '' ? 'border-warning' : ''}`}
                  >
                    <option value="">{`Choose a ${word.label.toLowerCase()}`}</option>
                    {word.options.map((option) => (
                      <option key={option.id} value={`v:${option.id}`}>
                        {option.name}
                      </option>
                    ))}
                    {word.canAdd && mayAddValues && (
                      <option value="new">{`Add “${word.word}” as a new ${word.label.toLowerCase()}`}</option>
                    )}
                    <option value="skip">Leave these rows out</option>
                  </select>
                </li>
              );
            })}
          </ul>
        </Card>
      </div>

      <div className="col-span-12 flex flex-col gap-3 lg:gap-4 xl:col-span-4">
        <Card>
          <SectionTitle>Designs</SectionTitle>
          <div className="flex justify-between text-sm text-gray-900">
            <span>Already in the catalogue</span>
            <b>{known}</b>
          </div>
          <p className="text-default mb-0 text-[13px]">
            Found by code, else by name in its category. Stock is added to them; their photos and
            sale prices stay as they are.
          </p>
          <div className="border-border-color mt-3 flex justify-between border-t pt-3 text-sm text-gray-900">
            <span>New — will be created</span>
            <b>{plan.newDesigns.length}</b>
          </div>
          {plan.newDesigns.length > 0 && (
            <p className="text-default mb-0 text-[13px]">
              {`${plan.newDesigns
                .slice(0, 2)
                .map((design) => `${design.name} (${design.code})`)
                .join(
                  ', ',
                )}${plan.newDesigns.length > 2 ? `, and ${plan.newDesigns.length - 2} more` : ''}. A new design with no code gets one made from its name.`}
            </p>
          )}
        </Card>
        {listed.some((word) => word.field === 'category') && (
          <Notice tone="info" title="A category the shop does not have yet">
            Make it on the Categories page, with the sizes and colours it takes, then come back — or
            leave its rows out for now.
          </Notice>
        )}
      </div>
    </div>
  );
}

/* ===================================================================== 4 · Check */

/** The check screen's counts, on the band. */
export function CheckCounts({ plan }: { plan: ImportPlan }) {
  const chip = 'flex items-baseline gap-2 rounded-xl px-4 py-2.5';

  return (
    <div className="mt-3 flex flex-wrap gap-2.5">
      <span className={`${chip} bg-[#ffffff]/10`}>
        <b className="font-serif text-2xl text-[#ffffff]">{plan.summary.read}</b>
        <span className="text-[13px] text-(--color-hero-muted)">rows read</span>
      </span>
      <span className={`${chip} bg-[#ffffff]/10`}>
        <b className="font-serif text-2xl text-[#ffffff]">{plan.summary.ready}</b>
        <span className="text-[13px] text-(--color-hero-muted)">ready</span>
      </span>
      {plan.summary.warning > 0 && (
        <span className={`${chip} bg-[#fdf3e6] text-[#8a4a04]`}>
          <b className="font-serif text-2xl">{plan.summary.warning}</b>
          <span className="text-[13px]">ready, with a warning</span>
        </span>
      )}
      {plan.summary.out > 0 && (
        <span className={`${chip} bg-[#fde8e8] text-[#991b1b]`}>
          <b className="font-serif text-2xl">{plan.summary.out}</b>
          <span className="text-[13px]">left out</span>
        </span>
      )}
      {plan.summary.done > 0 && (
        <span className={`${chip} bg-[#ffffff]/10`}>
          <b className="font-serif text-2xl text-[#ffffff]">{plan.summary.done}</b>
          <span className="text-[13px] text-(--color-hero-muted)">already in</span>
        </span>
      )}
    </div>
  );
}

const shownRows = 300;

const statusWords: Record<
  PlannedRow['status'],
  { label: string; tone: 'positive' | 'warning' | 'negative' | 'neutral' }
> = {
  ready: { label: 'Ready', tone: 'positive' },
  warning: { label: 'Warning', tone: 'warning' },
  out: { label: 'Left out', tone: 'negative' },
  done: { label: 'Already in', tone: 'neutral' },
};

export function CheckStep({
  session,
  plan,
  sheet,
  currency,
  branchName,
  delivery,
  pictures,
  onDelivery,
}: {
  session: Session;
  plan: ImportPlan;
  sheet: Sheet;
  currency: string;
  branchName: string;
  delivery: { purchasedOn: string; supplierVendorId: number | null; note: string };
  pictures: Map<string, File>;
  onDelivery: (delivery: {
    purchasedOn: string;
    supplierVendorId: number | null;
    note: string;
  }) => void;
}) {
  const needsLook = plan.rows.filter((row) => row.status === 'warning' || row.status === 'out');
  const [filter, setFilter] = useState<'look' | 'all'>(needsLook.length > 0 ? 'look' : 'all');
  const rows = filter === 'look' ? needsLook : plan.rows;
  const blocker = importBlocker(session, plan);
  const left = plan.rows.filter((row) => row.status === 'out');

  return (
    <div className="grid grid-cols-12 gap-3 lg:gap-4">
      <div className="col-span-12 xl:col-span-8">
        <Card flush className="py-3">
          <div className="flex flex-wrap items-center justify-between gap-2 px-5 pb-3">
            <div className="flex gap-2">
              {(
                [
                  ['look', `Needs a look · ${needsLook.length}`],
                  ['all', `All rows · ${plan.rows.length}`],
                ] as const
              ).map(([key, label]) =>
                key === filter ? (
                  <div
                    key={key}
                    className="bg-primary rounded-full px-3.5 py-1.5 text-[13px] font-semibold text-white"
                  >
                    {label}
                  </div>
                ) : (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setFilter(key)}
                    className="border-border-color hover:bg-light cursor-pointer rounded-full border bg-white px-3.5 py-1.5 text-[13px] text-gray-900"
                  >
                    {label}
                  </button>
                ),
              )}
            </div>
            {left.length > 0 && (
              <button
                type="button"
                className="text-primary cursor-pointer p-0 text-[13px] font-semibold"
                onClick={() => void downloadLeftOut(sheet, left)}
              >
                Download these as Excel, with the reason on each row
              </button>
            )}
          </div>
          <ul className="ps-0">
            {rows.slice(0, shownRows).map((row) => (
              <CheckRow key={row.number} row={row} pictures={pictures} />
            ))}
          </ul>
          {rows.length > shownRows && (
            <p className="text-default border-border-color mb-0 border-t px-5 pt-2.5 text-[13px]">
              {`And ${rows.length - shownRows} more rows — the download has every one left out.`}
            </p>
          )}
          {rows.length === 0 && (
            <p className="text-default border-border-color mb-0 border-t px-5 pt-2.5 text-[13px]">
              Nothing needs a look — every row is ready.
            </p>
          )}
        </Card>
      </div>

      <div className="col-span-12 flex flex-col gap-3 lg:gap-4 xl:col-span-4">
        {blocker && (
          <Notice tone="warning" title="Not ready to import">
            {blocker}
          </Notice>
        )}
        <Card>
          <SectionTitle>What will happen</SectionTitle>
          <Line label="New designs" value={String(plan.newDesigns.length)} />
          {plan.newValues.length > 0 && (
            <Line
              label="New sizes and colours"
              value={plan.newValues.map((value) => value.word).join(', ')}
            />
          )}
          <Line label="Garments booked in" value={String(plan.summary.garments)} />
          <Line label="Codes issued" value={String(plan.summary.garments - plan.summary.ownTags)} />
          {plan.summary.ownTags > 0 && (
            <Line label="Own tags kept" value={String(plan.summary.ownTags)} />
          )}
          {plan.summary.references > 0 && (
            <Line label="With a reference number" value={String(plan.summary.references)} />
          )}
          <Line label="Photos" value={String(plan.summary.pictures)} />
          {canAct(session, 'ItemUnit:ViewFinancials') && plan.summary.cost > 0 && (
            <div className="border-border-color mt-1 border-t pt-2">
              <Line
                label="Cost of the stock"
                value={`${currency}${formatAmount(plan.summary.cost)}`}
              />
            </div>
          )}
          <p className="text-default mt-2 mb-0 text-[13px]">
            {`At ${branchName}, a goods receipt for every hundred garments — each batch is booked in whole or not at all.`}
          </p>
        </Card>
        <DeliveryCard session={session} delivery={delivery} onChange={onDelivery} />
        <Card>
          <p className="mb-1 text-sm font-semibold text-gray-900">Left out is not lost</p>
          <p className="text-default mb-0 text-[13px]">
            Fix those rows in the sheet and import it again: rows already in are recognised and
            skipped, so nothing comes in twice.
          </p>
        </Card>
      </div>
    </div>
  );
}

function Line({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3 py-1 text-sm text-gray-900">
      <span>{label}</span>
      <b className="text-end">{value}</b>
    </div>
  );
}

function CheckRow({ row, pictures }: { row: PlannedRow; pictures: Map<string, File> }) {
  const status = statusWords[row.status];
  const file = row.picture?.startsWith('pic:') ? pictures.get(row.picture) : undefined;
  const [thumb, setThumb] = useState<string | null>(null);

  useEffect(() => {
    if (!file) {
      return undefined;
    }

    const url = URL.createObjectURL(file);
    setThumb(url);

    return () => URL.revokeObjectURL(url);
  }, [file]);

  const detail = [row.look, `${row.quantity} garment${row.quantity === 1 ? '' : 's'}`]
    .filter((part) => part !== '')
    .join(' · ');

  return (
    <li className="border-border-color flex list-none items-center gap-3 border-t px-5 py-2.5">
      <span className="text-default w-14 shrink-0 font-mono text-[12px]">{`Row ${row.number}`}</span>
      <span className="bg-light flex h-10 w-8 shrink-0 items-center justify-center overflow-hidden rounded">
        {thumb ? (
          <img src={thumb} alt="" className="size-full object-cover" />
        ) : row.picture ? (
          <i className="icon icon-image text-default text-[13px]" />
        ) : null}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-xs font-semibold text-gray-900">
          {row.designName || '(no design)'}
        </span>
        <span className="text-default block truncate text-[11px]">{detail}</span>
        {/* Below 640px the reason sits under the row rather than beside it — never dropped. */}
        {row.reasons.length > 0 && (
          <span
            className={`mt-0.5 block text-[12px] sm:hidden ${
              row.status === 'out'
                ? 'text-danger'
                : row.status === 'warning'
                  ? 'text-warning'
                  : 'text-default'
            }`}
          >
            {row.reasons.join(' · ')}
          </span>
        )}
      </span>
      <span
        className={`hidden min-w-0 flex-[1.3] text-[13px] sm:block ${
          row.status === 'out'
            ? 'text-danger'
            : row.status === 'warning'
              ? 'text-warning'
              : 'text-default'
        }`}
      >
        {row.reasons.join(' · ')}
      </span>
      <StatusBadge tone={status.tone}>{status.label}</StatusBadge>
    </li>
  );
}

function DeliveryCard({
  session,
  delivery,
  onChange,
}: {
  session: Session;
  delivery: { purchasedOn: string; supplierVendorId: number | null; note: string };
  onChange: (delivery: {
    purchasedOn: string;
    supplierVendorId: number | null;
    note: string;
  }) => void;
}) {
  const [supplier, setSupplier] = useState<PickerOption | null>(null);
  const [search, setSearch] = useState('');
  const suppliers = useVendors(session.branch?.id, {
    capability: 'Supplier',
    search,
    pageSize: 10,
  });
  const options: PickerOption[] = (suppliers.data?.items ?? []).map((vendor) => ({
    id: String(vendor.id),
    primary: vendor.name,
    secondary: vendor.mobile,
  }));

  return (
    <Card>
      <SectionTitle>The delivery</SectionTitle>
      <div className="flex flex-col gap-3">
        <Field label="Came in on">
          <DatePicker
            className="w-full"
            value={toDayjs(delivery.purchasedOn) ?? null}
            onChange={(value) => onChange({ ...delivery, purchasedOn: toIsoDate(value) ?? '' })}
          />
        </Field>
        <Field label="Supplier">
          <SearchPicker
            value={supplier}
            options={options}
            total={suppliers.data?.total ?? 0}
            loading={suppliers.isLoading}
            placeholder="Search suppliers"
            onSearch={setSearch}
            onSelect={(option) => {
              setSupplier(option);
              onChange({
                ...delivery,
                supplierVendorId: option === null ? null : Number(option.id),
              });
            }}
          />
        </Field>
        <Field label="Invoice or note">
          <input
            value={delivery.note}
            onChange={(event) => onChange({ ...delivery, note: event.target.value })}
            placeholder="Optional"
            className={controlClass}
          />
        </Field>
      </div>
    </Card>
  );
}

function downloadLeftOut(sheet: Sheet, left: readonly PlannedRow[]) {
  const byNumber = new Map(sheet.rows.map((row) => [row.number, row]));

  return saveWorkbook(
    'rows-left-out.xlsx',
    'Left out',
    ['Row', ...sheet.headers, 'Why it was left out'],
    left.map((row) => [
      String(row.number),
      ...(byNumber.get(row.number)?.cells ?? []),
      row.reasons.join('; '),
    ]),
  );
}
